import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsAPI, mouvementsAPI } from '../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    Promise.all([statsAPI.get(), mouvementsAPI.getAll({ limit: 8 })])
      .then(([s, m]) => {
        setStats(s.data);
        setMouvements(m.data);
        setLastUpdate(new Date());
      })
      .catch(err => console.error('Dashboard error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <div className="loading" style={{ minHeight: 300 }}>
      <div className="spinner"></div>Chargement...
    </div>
  );

  const CARDS = [
    { label: 'Équipements', value: stats?.totalEquipements || 0, icon: '📦', color: 'var(--blue)', bg: 'var(--blue-bg)', link: '/inventaire' },
    { label: 'Catégories', value: stats?.totalCategories || 0, icon: '🗂', color: 'var(--purple)', bg: 'var(--purple-bg)', link: '/categories' },
    { label: 'Alertes stock', value: stats?.alertes || 0, icon: '⚠️', color: stats?.alertes > 0 ? 'var(--red)' : 'var(--green)', bg: stats?.alertes > 0 ? 'var(--red-bg)' : 'var(--green-bg)', link: '/inventaire?alerte=true' },
    { label: 'Mouvements 7j', value: stats?.mouvementsRecents || 0, icon: '🔄', color: 'var(--amber)', bg: 'var(--amber-bg)', link: '/mouvements' },
  ];

  const valeur = stats?.valeurTotale || 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="page-title">Tableau de bord</div>
          <div className="page-subtitle">Vue générale du stock d'équipements réseau</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button className="btn btn-outline btn-sm" onClick={fetchData}>↺ Actualiser</button>
        </div>
      </div>

      {/* Alerte banner */}
      {stats?.alertes > 0 && (
        <div className="alert-banner" onClick={() => navigate('/inventaire')} style={{ cursor: 'pointer' }}>
          ⚠️ {stats.alertes} équipement(s) sous le seuil minimal — Cliquez pour voir
        </div>
      )}

      {/* Stat cards */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {CARDS.map(c => (
          <div key={c.label} className="stat-card"
            onClick={() => navigate(c.link)}
            style={{ cursor: 'pointer', borderTop: `3px solid ${c.color}` }}>
            <div className="stat-label">{c.label}</div>
            <div className="stat-value" style={{ color: c.color }}>{c.value}</div>
            <div className="stat-icon" style={{ background: c.bg }}>{c.icon}</div>
          </div>
        ))}
      </div>

      {/* Valeur totale stock */}
      {valeur > 0 && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '14px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
            💰 Valeur totale du stock
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>
            {valeur.toLocaleString('fr-FR')} FCFA
          </div>
        </div>
      )}

      {/* Actions rapides */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Actions rapides
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/inventaire')}>+ Ajouter équipement</button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/mouvements')}>⇅ Voir mouvements</button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/categories')}>🗂 Gérer catégories</button>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/inventaire?alerte=true')}>⚠️ Alertes stock</button>
        </div>
      </div>

      {/* Grille infos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Mouvements récents */}
        <div className="table-container">
          <div className="table-toolbar" style={{ justifyContent: 'space-between' }}>
            <span className="toolbar-title">Mouvements récents</span>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/mouvements')}>Voir tout →</button>
          </div>
          {mouvements.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🔄</div>Aucun mouvement</div>
          ) : (
            <table>
              <thead>
                <tr><th>Date</th><th>Équipement</th><th>Type</th><th>Qté</th></tr>
              </thead>
              <tbody>
                {mouvements.map(m => (
                  <tr key={m.id || m._id} style={{ cursor: 'pointer' }} onClick={() => navigate('/mouvements')}>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {m.created_at ? format(new Date(m.created_at), 'dd/MM HH:mm', { locale: fr }) : '—'}
                    </td>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{m.equipement?.nom || m.equipements?.nom || '—'}</td>
                    <td><span className={`badge badge-${m.type}`}>{m.type}</span></td>
                    <td style={{ fontWeight: 700, color: m.type === 'entrée' ? 'var(--green)' : 'var(--red)' }}>
                      {m.type === 'entrée' ? '+' : '-'}{m.quantite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Stock par catégorie */}
        <div className="table-container">
          <div className="table-toolbar" style={{ justifyContent: 'space-between' }}>
            <span className="toolbar-title">Stock par catégorie</span>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/categories')}>Gérer →</button>
          </div>
          {!stats?.parCategorie?.length ? (
            <div className="empty-state"><div className="empty-icon">🗂</div>Aucune catégorie</div>
          ) : (
            <table>
              <thead>
                <tr><th>Catégorie</th><th>Articles</th><th>Quantité</th></tr>
              </thead>
              <tbody>
                {stats.parCategorie.map((c, i) => (
                  <tr key={c._id || i} style={{ cursor: 'pointer' }} onClick={() => navigate('/inventaire')}>
                    <td>
                      <span style={{ marginRight: 6 }}>{c.cat?.icone || '📦'}</span>
                      <span style={{ fontWeight: 500 }}>{c.cat?.nom || 'Sans catégorie'}</span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.count}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: 'var(--blue)', fontSize: 15 }}>{c.total}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
